import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AREAS } from '@bb/shared';
import { social, type SocialDto } from '../net/api';
import { wearFrom } from '../art/avatar';
import { drawFurniture } from '../art/items';
import { AvatarPreview } from './AvatarPreview';
import { Sheet } from './Sheet';

export function Friends({ catalog, onClose, onProfile, onGift }: { catalog: Map<string, { render: Record<string, unknown> }>; onClose: () => void; onProfile: (name: string) => void; onGift?: (username: string) => void }) {
  const [data, setData] = useState<SocialDto>();
  const [name, setName] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [visiting, setVisiting] = useState<string>();

  const load = () => social.overview().then(setData, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  useEffect(() => {
    void load();
    const t = window.setInterval(load, 15_000);
    return () => window.clearInterval(t);
  }, []);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) setMsg({ kind: 'ok', text: ok });
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  };

  function add(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    void act(async () => {
      const r = await social.request(name.trim());
      setMsg({
        kind: 'ok',
        text:
          r.status === 'friends' ? `You and ${r.displayName} are now friends!`
          : r.status === 'awaiting_parent' ? `Your request to ${r.displayName} needs a parent's OK first. We've let them know.`
          : `Friend request sent to ${r.displayName}.`,
      });
      setName('');
    });
  }

  if (visiting) return <HomeViewer owner={visiting} onBack={() => setVisiting(undefined)} onClose={onClose} />;

  return (
    <Sheet title="Friends" subtitle={data ? `${data.friends.filter((f) => f.online).length} online · ${data.friends.length} friends` : 'Loading…'} onClose={onClose} wide>
      <form className="row-wrap" onSubmit={add}>
        <input id="friend-name" className="grow" placeholder="Add a friend by their username" value={name} onChange={(e) => setName(e.target.value)} maxLength={32} aria-label="Username" />
        <button className="btn" disabled={!name.trim()}>Send request</button>
      </form>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {data && data.incoming.length > 0 && (
        <section className="stack">
          <h3 className="mini-head">Requests for you</h3>
          {data.incoming.map((r) => (
            <div key={r.id} className="friend-row">
              <span className="stack tight">
                <strong>{r.username}</strong>
                {r.displayName && <button className="link-btn small-print" onClick={() => onProfile(r.displayName!)}>playing as {r.displayName}</button>}
                {r.viaInvite && <span className="muted small-print">Joined with your invite code</span>}
              </span>
              <span className="spacer" />
              <button className="btn small" onClick={() => void act(() => social.respond(r.id, true), `You're now friends with ${r.username}.`)}>Accept</button>
              <button className="btn small ghost" onClick={() => void act(() => social.respond(r.id, false))}>Decline</button>
            </div>
          ))}
        </section>
      )}
      <section className="friend-grid">
        {data?.friends.length === 0 && <p className="muted">No friends yet. Click someone in town to see their profile and add them.</p>}
        {data?.friends.map((f) => (
          <article key={f.username} className={f.online ? 'friend-card on' : 'friend-card'}>
            {f.characterType && <AvatarPreview look={{ characterType: f.characterType, species: f.species, appearance: f.appearance, wear: wearFrom(f.equipped, catalog) }} scale={0.8} />}
            <div className="stack tight">
              <strong>{f.username}</strong>
              {f.displayName ? <button className="link-btn small-print" onClick={() => onProfile(f.displayName!)}>playing as {f.displayName}</button> : <span className="muted small-print">No character yet</span>}
              <span className="muted small-print">{f.online ? `Online · ${AREAS[f.area ?? '']?.name ?? 'Buddy Bay'}` : 'Offline'}</span>
              <span className="row-wrap tight">
                <button className="btn small ghost" onClick={() => setVisiting(f.username)}>Visit home</button>
                {onGift && <button className="btn small ghost" onClick={() => onGift(f.username)}>Send a gift</button>}
              </span>
            </div>
          </article>
        ))}
      </section>
      {data && data.outgoing.length > 0 && (
        <section className="stack">
          <h3 className="mini-head">Waiting for an answer</h3>
          {data.outgoing.map((r) => (
            <div key={r.id} className="friend-row">
              <span>{r.username}{r.displayName && <span className="muted small-print"> · playing as {r.displayName}</span>}</span>
              {r.waitingForParent && <span className="tag">waiting for a parent</span>}
              <span className="spacer" />
              <button className="btn small ghost" onClick={() => void act(() => social.cancel(r.id))}>Cancel</button>
            </div>
          ))}
        </section>
      )}
      {data && data.blocked.length > 0 && (
        <section className="stack">
          <h3 className="mini-head">Blocked</h3>
          {data.blocked.map((n) => (
            <div key={n} className="friend-row">
              <span>{n}</span>
              <span className="spacer" />
              <button className="btn small ghost" onClick={() => void act(() => social.unblock(n), `Unblocked ${n}.`)}>Unblock</button>
            </div>
          ))}
        </section>
      )}
    </Sheet>
  );
}

function HomeViewer({ owner, onBack, onClose }: { owner: string; onBack: () => void; onClose: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string>();
  useEffect(() => {
    social.visit(owner).then((home) => {
      const el = ref.current;
      if (!el) return;
      const W = 960;
      const H = 600;
      const dpr = window.devicePixelRatio || 1;
      el.width = W * dpr;
      el.height = H * dpr;
      const c = el.getContext('2d')!;
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.fillStyle = '#f6e7cf';
      c.fillRect(0, 0, W, 150);
      c.fillStyle = '#9fdde6';
      c.fillRect(W / 2 - 90, 24, 180, 96);
      c.fillStyle = '#d9b48a';
      c.fillRect(0, 150, W, H - 150);
      c.fillStyle = '#c99f72';
      for (let y = 180; y < H; y += 30) c.fillRect(0, y, W, 2);
      for (const p of [...home.placements].sort((a, b) => a.y - b.y)) {
        c.save();
        c.translate(p.x, p.y);
        c.rotate((p.rotation * Math.PI) / 180);
        drawFurniture(c, p.render);
        c.restore();
      }
    }, (e: Error) => setError(e.message));
  }, [owner]);
  return (
    <Sheet title={`${owner}'s Cove Cottage`} subtitle={<button className="link-btn" onClick={onBack}>← Back to friends</button>} onClose={onClose} wide>
      {error ? <p className="form-error">{error}</p> : <canvas ref={ref} className="visit-canvas" style={{ aspectRatio: '960 / 600' }} aria-label={`${owner}'s home`} role="img" />}
    </Sheet>
  );
}
